import React from 'react';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Avatar,
  Tabs,
  Row,
  Col,
  Statistic,
  Tag,
  Spin,
} from 'antd';
import {
  EditOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { usePartner } from '../hooks';
import { PartnerStatusBadge } from '../components/shared';
import { getInitials } from '../utils/helpers';
import ClinicsTab from './ClinicsTab';
import DoctorsTab from './DoctorsTab';
import moment from 'moment';

const PartnerDetails = () => {
  const history = useHistory();
  const { partner_id } = useParams();

  const { data, isLoading } = usePartner(partner_id);

  const partner = data?.data || data;

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="Loading partner details..." />
      </div>
    );
  }

  if (!partner) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <p>Partner not found</p>
        <Button onClick={() => history.push('/b2b/partners')}>
          Back to Partners
        </Button>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'info',
      label: 'Partner Information',
      children: (
        <Card>
          <Descriptions bordered column={{ xs: 1, sm: 1, md: 2 }}>
            <Descriptions.Item label="Partner Name">
              {partner.name}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <PartnerStatusBadge status={partner.status} />
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <Space>
                <MailOutlined />
                {partner.email}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              <Space>
                <PhoneOutlined />
                {partner.phone}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {partner.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {moment(partner.created_at).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {moment(partner.updated_at).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          </Descriptions>

          {partner.maib_merchant_id && (
            <>
              <Card
                title={
                  <Space>
                    <BankOutlined />
                    MAIB Payment Configuration
                  </Space>
                }
                style={{ marginTop: 24 }}
                size="small"
              >
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Merchant ID">
                    {partner.maib_merchant_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Merchant Name">
                    {partner.maib_merchant_name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Terminal ID">
                    {partner.maib_terminal_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Signature Key">
                    <Tag color="blue">Configured</Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </>
          )}
        </Card>
      ),
    },
    {
      key: 'clinics',
      label: (
        <span>
          <BankOutlined />
          Clinics ({partner.clinics_count || 0})
        </span>
      ),
      children: <ClinicsTab partnerId={partner_id} />,
    },
    {
      key: 'doctors',
      label: (
        <span>
          <MedicineBoxOutlined />
          Doctors ({partner.doctors_count || 0})
        </span>
      ),
      children: <DoctorsTab partnerId={partner_id} />,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => history.push('/b2b/partners')}
                type="text"
                aria-label="Back to partners list"
              />
              <Avatar
                size={64}
                src={partner.logo}
                icon={!partner.logo && <UserOutlined />}
                style={{ backgroundColor: partner.logo ? 'transparent' : '#1890ff' }}
              >
                {!partner.logo && getInitials(partner.name)}
              </Avatar>
              <div>
                <h2 style={{ margin: 0 }}>{partner.name}</h2>
                <PartnerStatusBadge status={partner.status} />
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => history.push(`/b2b/partners/${partner_id}/edit`)}
            >
              Edit Partner
            </Button>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Clinics"
                value={partner.clinics_count || 0}
                prefix={<BankOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Doctors"
                value={partner.doctors_count || 0}
                prefix={<MedicineBoxOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Consultations"
                value={partner.consultations_count || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Tabs defaultActiveKey="info" items={tabItems} />
      </Card>
    </div>
  );
};

export default PartnerDetails;
